import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  BookOpen,
  Bot,
  CheckCircle2,
  Gift,
  ListChecks,
  Plus,
  RefreshCw,
  Save,
  Search,
  Shield,
  Skull,
  Swords,
  Terminal,
  Timer,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const empty = {
  trigger: "",
  command_type: "text_response",
  response: "",
  ai_prompt: "",
  cooldown_seconds: 5,
  enabled: true,
  mod_only: false,
};
const inputClass = "w-full h-10 bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 text-sm outline-none focus:border-[hsl(var(--primary))]";
const compactInputClass = "w-full h-9 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded px-2 text-sm outline-none focus:border-[hsl(var(--primary))]";

// Triggers use "!" as a placeholder; the page rewrites them with Settings command_prefix.
const builtInGroups = [
  {
    title: "Core Chat",
    icon: Terminal,
    tone: "text-[hsl(var(--primary))]",
    commands: [
      { trigger: "!commands", purpose: "Lists enabled editable commands.", owner: "Editable response" },
      { trigger: "!points", purpose: "Shows the viewer's Bunny, Rat, Giveaway, and Event Token balances.", owner: "Editable response" },
      { trigger: "!interact", purpose: "Sends viewers to Viewer Redeems and reminds them about !redeem.", owner: "Editable response" },
      { trigger: "!profile", purpose: "Links the viewer profile page.", owner: "Editable response" },
      { trigger: "!leaderboard", purpose: "Links the public leaderboard.", owner: "Editable response" },
      { trigger: "!hype", purpose: "Shows the current stream hype level and score.", owner: "Built-in handler" },
      { trigger: "!watchtime", purpose: "Shows the viewer's active watchtime.", owner: "Built-in handler" },
      { trigger: "!redeem <key>", purpose: "Creates a redeem request for an enabled interaction key.", owner: "Built-in handler" },
      { trigger: "!ask <question>", purpose: "Asks TheBarracksBunny to answer in the configured chatbot personality.", owner: "AI personality" },
      { trigger: "@TheBarracksBunny", purpose: "Mention reply path for personality chat when enabled.", owner: "AI personality" },
    ],
  },
  {
    title: "Giveaways",
    icon: Gift,
    tone: "text-[hsl(var(--currency-free))]",
    commands: [
      { trigger: "!giveaway", purpose: "Enters the current giveaway once, when one is open.", owner: "Built-in handler" },
      { trigger: "!giveaway <entries>", purpose: "Buys multiple entries with Giveaway Tokens.", owner: "Built-in handler" },
      { trigger: "!giveaway join <entries>", purpose: "Alternate entry form for the same active giveaway.", owner: "Built-in handler" },
      { trigger: "!giveaways", purpose: "Alias for !giveaway.", owner: "Built-in handler" },
    ],
  },
  {
    title: "Coworking",
    icon: Timer,
    tone: "text-[#64D2FF]",
    commands: [
      { trigger: "!coworking", purpose: "Explains the focus board and lists coworking commands. Suite must be enabled in Admin → Coworking.", owner: "Viewer" },
      { trigger: "!task <text>", purpose: "Adds an open task for the viewer on the coworking board.", owner: "Viewer" },
      { trigger: "!done", purpose: "Completes your latest open coworking task.", owner: "Viewer" },
      { trigger: "!done <n>", purpose: "Completes your task by board index (or your own list index).", owner: "Viewer" },
      { trigger: "!tasks", purpose: "Lists open coworking tasks in chat.", owner: "Viewer" },
      { trigger: "!focus start|pause|reset|skip|status", purpose: "Controls the shared focus timer (start, pause, reset, skip phase, or status).", owner: "Mod / broadcaster" },
      { trigger: "!cleartasks", purpose: "Clears the coworking task board.", owner: "Mod / broadcaster" },
    ],
  },
  {
    title: "Admin And Mods",
    icon: Shield,
    tone: "text-sky-300",
    commands: [
      { trigger: "!so <login>", purpose: "Sends a one-off AI/fallback creator shoutout and attempts Twitch's native shoutout.", owner: "Built-in mod handler" },
      { trigger: "!shoutout <login>", purpose: "Alias for !so.", owner: "Built-in mod handler" },
      { trigger: "!autoshoutout add <login>", purpose: "Adds a creator to the auto-shoutout list with manual source.", owner: "Built-in mod handler" },
      { trigger: "!autoshoutout remove <login>", purpose: "Removes a creator from the auto-shoutout list.", owner: "Built-in mod handler" },
      { trigger: "!autoshoutout list", purpose: "Lists enabled auto-shoutout creators.", owner: "Built-in mod handler" },
      { trigger: "!autoshoutout test <login>", purpose: "Sends a test shoutout for a configured creator.", owner: "Built-in mod handler" },
    ],
  },
  {
    title: "RPG And Dungeons",
    icon: Skull,
    tone: "text-[hsl(var(--currency-event))]",
    commands: [
      { trigger: "!quest", purpose: "Shows the simple RPG quest options and your character level.", owner: "Built-in handler" },
      { trigger: "!quest free", purpose: "Starts your one free generated quest for the day.", owner: "Built-in handler" },
      { trigger: "!quest bunny500", purpose: "Starts a generated quest funded with Bunny Tokens.", owner: "Built-in handler" },
      { trigger: "!quest rat100", purpose: "Starts a generated quest funded with Rat Coins.", owner: "Built-in handler" },
      { trigger: "!quest list", purpose: "Lists active special or seasonal quests.", owner: "Built-in handler" },
      { trigger: "!quest <slug>", purpose: "Starts a special quest from the active list.", owner: "Built-in handler" },
      { trigger: "!dungeon <slug>", purpose: "Legacy alias path for starting a configured dungeon run.", owner: "Built-in handler" },
      { trigger: "!join", purpose: "Joins the currently assembling party.", owner: "Built-in handler" },
      { trigger: "!stats", purpose: "Shows the viewer's RPG skill levels.", owner: "Built-in handler" },
      { trigger: "!skills", purpose: "Alias for !stats.", owner: "Built-in handler" },
      { trigger: "!inv", purpose: "Shows inventory count and profile link.", owner: "Built-in handler" },
      { trigger: "!inventory", purpose: "Alias for !inv.", owner: "Built-in handler" },
    ],
  },
  {
    title: "RPG Event Keywords",
    icon: Swords,
    tone: "text-emerald-400",
    commands: [
      { trigger: "!gather", purpose: "Default event keyword for gathering materials when that RPG event is active.", owner: "RPG event config" },
      { trigger: "!scavenge", purpose: "Default event keyword for scavenging rewards when that RPG event is active.", owner: "RPG event config" },
      { trigger: "!<event keyword>", purpose: "Any active RPG event can expose its configured command keyword.", owner: "RPG event config" },
    ],
  },
];

function applyCommandPrefix(groups, prefix) {
  const p = prefix || "!";
  return groups.map((group) => ({
    ...group,
    commands: group.commands.map((cmd) => ({
      ...cmd,
      trigger: cmd.trigger.startsWith("!") ? `${p}${cmd.trigger.slice(1)}` : cmd.trigger,
    })),
  }));
}

const variables = [
  { token: "{user}", description: "Viewer display name from the chat message." },
  { token: "{args}", description: "Text typed after the command trigger." },
  { token: "{free}", description: "Viewer Bunny Token balance." },
  { token: "{paid}", description: "Viewer Rat Token balance." },
  { token: "{giveaway}", description: "Viewer Giveaway Token balance." },
  { token: "{event}", description: "Viewer Event Token balance." },
  { token: "{free_name}", description: "Configured display name for Bunny Tokens." },
  { token: "{paid_name}", description: "Configured display name for Rat Tokens." },
  { token: "{giveaway_name}", description: "Configured display name for Giveaway Tokens." },
  { token: "{event_name}", description: "Configured display name for Event Tokens." },
  { token: "{list}", description: "Comma-separated list of enabled editable commands." },
  { token: "{viewer_url}", description: "Legacy viewer URL for the chatter login." },
  { token: "{interact_url}", description: "Public Viewer Redeems page URL." },
  { token: "{leaderboard_url}", description: "Public leaderboard page URL." },
  { token: "{profile_url}", description: "Viewer profile page URL." },
];

export default function Commands() {
  const [cmds, setCmds] = useState([]);
  const [form, setForm] = useState(empty);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [commandPrefix, setCommandPrefix] = useState("!");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, settingsResult] = await Promise.all([
        api.get("/commands"),
        api.get("/settings").catch(() => ({ data: null })),
      ]);
      setCmds(data || []);
      const prefix = settingsResult?.data?.command_prefix;
      if (typeof prefix === "string" && prefix.trim()) {
        setCommandPrefix(prefix.trim());
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const prefixedBuiltIns = useMemo(
    () => applyCommandPrefix(builtInGroups, commandPrefix),
    [commandPrefix],
  );

  const create = async (e) => {
    e.preventDefault();
    try {
      const command_type = form.command_type || "text_response";
      await api.post("/commands", {
        ...form,
        command_type,
        response: command_type === "ai_prompt" ? "" : form.response,
        ai_prompt: command_type === "ai_prompt" ? form.ai_prompt : "",
      });
      toast.success(`Created !${form.trigger}`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  };

  const save = async (c) => {
    await api.patch(`/commands/${c.id}`, {
      trigger: c.trigger,
      command_type: c.command_type || "text_response",
      response: c.response,
      ai_prompt: c.ai_prompt || "",
      cooldown_seconds: c.cooldown_seconds,
      enabled: c.enabled,
      mod_only: c.mod_only,
    });
    toast.success(`Saved !${c.trigger}`);
    load();
  };

  const del = async (c) => {
    if (!window.confirm(`Delete !${c.trigger}?`)) return;
    await api.delete(`/commands/${c.id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return cmds.filter((cmd) => {
      const matchesText = !needle || [cmd.trigger, cmd.response, cmd.ai_prompt, cmd.command_type].some((value) => String(value || "").toLowerCase().includes(needle));
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "enabled" && cmd.enabled) ||
        (statusFilter === "disabled" && !cmd.enabled) ||
        (statusFilter === "mod" && cmd.mod_only);
      return matchesText && matchesStatus;
    });
  }, [cmds, query, statusFilter]);

  const enabledCount = cmds.filter((cmd) => cmd.enabled).length;
  const modOnlyCount = cmds.filter((cmd) => cmd.mod_only).length;
  const aiCommandCount = cmds.filter((cmd) => (cmd.command_type || "text_response") === "ai_prompt").length;
  return (
    <div className="space-y-6" data-testid="commands-page">
      <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Chat Bot</div>
          <h1 className="text-3xl font-semibold tracking-normal mt-1">Commands</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-3xl">
            Editable responses live here; bot-owned commands are listed below so dungeon, giveaway, redeem, coworking, and RPG event behavior is not hidden anymore. Triggers use Settings command prefix ({commandPrefix}).
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="h-10 px-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] text-sm inline-flex items-center gap-2 self-start xl:self-auto"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Stat icon={Terminal} label="Editable" value={cmds.length} />
        <Stat icon={CheckCircle2} label="Enabled" value={enabledCount} />
        <Stat icon={Shield} label="Mod only" value={modOnlyCount} />
        <Stat icon={Bot} label="AI prompt" value={aiCommandCount} />
      </section>

      <section className="surface p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="label-eyebrow">Create</div>
            <h2 className="text-lg font-semibold mt-1">Editable Chat Command</h2>
          </div>
          <span className="text-xs text-[hsl(var(--muted-foreground))] font-mono">Stored in /commands</span>
        </div>
        <form onSubmit={create} className="grid grid-cols-1 lg:grid-cols-12 gap-3" data-testid="new-command-form">
          <Field label="Trigger" className="lg:col-span-2">
            <TriggerInput value={form.trigger} onChange={(trigger) => setForm({ ...form, trigger })} testId="cmd-trigger-input" />
          </Field>
          <Field label="Type" className="lg:col-span-2">
            <CommandTypeSelect value={form.command_type} onChange={(command_type) => setForm({ ...form, command_type })} />
          </Field>
          <Field label={form.command_type === "ai_prompt" ? "AI Prompt" : "Response"} className="lg:col-span-5">
            {form.command_type === "ai_prompt" ? (
              <textarea
                value={form.ai_prompt}
                onChange={(e) => setForm({ ...form, ai_prompt: e.target.value })}
                required
                rows={3}
                data-testid="cmd-ai-prompt-input"
                className={`${inputClass} h-20 py-2 resize-y`}
                placeholder="Answer {args} as TheBarracksBunny in a short, stream-safe style."
              />
            ) : (
              <input
                value={form.response}
                onChange={(e) => setForm({ ...form, response: e.target.value })}
                required
                data-testid="cmd-response-input"
                className={inputClass}
              />
            )}
          </Field>
          <Field label="Cooldown" className="lg:col-span-1">
            <NumberInput value={form.cooldown_seconds} onChange={(cooldown_seconds) => setForm({ ...form, cooldown_seconds })} />
          </Field>
          <Field label="Options" className="lg:col-span-1">
            <div className="h-10 flex items-center gap-3 text-xs">
              <CheckboxLabel label="Enabled" checked={form.enabled} onChange={(enabled) => setForm({ ...form, enabled })} />
              <CheckboxLabel label="Mod" checked={form.mod_only} onChange={(mod_only) => setForm({ ...form, mod_only })} />
            </div>
          </Field>
          <div className="lg:col-span-1 flex items-end">
            <button type="submit" data-testid="create-command-btn" className="h-10 w-full bg-[hsl(var(--primary))] text-white text-sm px-3 rounded inline-flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </form>
      </section>

      <section className="surface p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow">Manage</div>
            <h2 className="text-lg font-semibold mt-1">Editable Commands</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="h-10 w-full sm:w-64 pl-9 pr-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]"
              />
            </div>
            <div className="inline-flex rounded border border-[hsl(var(--border))] overflow-hidden h-10">
              {["all", "enabled", "disabled", "mod"].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setStatusFilter(option)}
                  className={`px-3 text-xs capitalize ${statusFilter === option ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--surface-2))] text-[hsl(var(--muted-foreground))]"}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1040px]">
            <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="py-3 pr-3">Trigger</th>
                <th className="w-44 px-3">Type</th>
                <th className="px-3">Response / AI prompt</th>
                <th className="w-28 px-3">Cooldown</th>
                <th className="w-32 px-3">Flags</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <Row key={c.id} cmd={c} onSave={save} onDelete={del} />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[hsl(var(--muted-foreground))]">
                    No editable commands matched.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          <div>
            <div className="label-eyebrow">Reference</div>
            <h2 className="text-lg font-semibold mt-1">Built-in Bot Commands</h2>
          </div>
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
            {prefixedBuiltIns.map((group) => (
              <BuiltInGroup
                key={group.title}
                group={group}
                commandPrefix={commandPrefix}
                editableTriggers={cmds.map((cmd) => cmd.trigger)}
              />
            ))}
          </div>
        </div>
        <aside className="surface p-5 h-fit">
          <div className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-[hsl(var(--primary))]" />
            <div>
              <div className="label-eyebrow">Template Fields</div>
              <h2 className="text-lg font-semibold mt-1">Response Variables</h2>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {variables.map((variable) => (
              <div key={variable.token} className="border-t border-[hsl(var(--border))] pt-3 first:border-t-0 first:pt-0">
                <code className="text-xs px-2.5 py-1.5 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-white">
                  {variable.token}
                </code>
                <p className="mt-2 text-sm leading-6 text-[hsl(var(--muted-foreground))]">{variable.description}</p>
              </div>
            ))}
          </div>
        </aside>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label = "", value } = {}) {
  return (
    <div className="surface p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[hsl(var(--primary))]" />
      </div>
      <div>
        <div className="text-2xl font-semibold leading-none">{value}</div>
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-1">{label}</div>
      </div>
    </div>
  );
}

function BuiltInGroup({ group, editableTriggers, commandPrefix = "!" }) {
  const Icon = group.icon;
  const editableSet = new Set(editableTriggers);
  const prefix = commandPrefix || "!";
  return (
    <div className="surface p-5" data-testid={`built-in-group-${group.title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-4">
        <Icon className={`w-5 h-5 ${group.tone}`} />
        <h3 className="font-semibold">{group.title}</h3>
      </div>
      <div className="space-y-3">
        {group.commands.map((cmd) => {
          const withoutPrefix = cmd.trigger.startsWith(prefix) ? cmd.trigger.slice(prefix.length) : cmd.trigger.replace(/^!/, "");
          const rawTrigger = withoutPrefix.split(" ")[0];
          const isEditable = editableSet.has(rawTrigger);
          return (
            <div key={cmd.trigger} className="border-t border-[hsl(var(--border))] pt-3 first:border-t-0 first:pt-0">
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-sm text-white">{cmd.trigger}</code>
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[hsl(var(--surface-2))] text-[hsl(var(--muted-foreground))]">
                  {cmd.owner}
                </span>
                {isEditable ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400"><CheckCircle2 className="w-3 h-3" /> Editable</span>
                ) : (
                  cmd.owner === "Editable response" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]"><XCircle className="w-3 h-3" /> Missing</span>
                )}
              </div>
              <p className="mt-1 text-sm leading-6 text-[hsl(var(--muted-foreground))]">{cmd.purpose}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ cmd, onSave, onDelete }) {
  const [c, setC] = useState(cmd);
  useEffect(() => setC(cmd), [cmd]);
  const dirty = JSON.stringify(c) !== JSON.stringify(cmd);
  const commandType = c.command_type || "text_response";
  return (
    <tr className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]">
      <td className="py-2 pr-3 font-mono">
        <TriggerInput value={c.trigger} onChange={(trigger) => setC({ ...c, trigger })} compact />
      </td>
      <td className="px-3">
        <CommandTypeSelect value={commandType} onChange={(command_type) => setC({ ...c, command_type })} compact />
      </td>
      <td className="px-3 py-2">
        {commandType === "ai_prompt" ? (
          <textarea
            value={c.ai_prompt || ""}
            onChange={(e) => setC({ ...c, ai_prompt: e.target.value })}
            rows={2}
            className={`${compactInputClass} h-16 py-2 resize-y`}
          />
        ) : (
          <input value={c.response || ""} onChange={(e) => setC({ ...c, response: e.target.value })} className={compactInputClass} />
        )}
      </td>
      <td className="px-3">
        <NumberInput value={c.cooldown_seconds} onChange={(cooldown_seconds) => setC({ ...c, cooldown_seconds })} compact />
      </td>
      <td className="px-3">
        <div className="flex flex-col text-xs gap-1">
          <CheckboxLabel label="on" checked={c.enabled} onChange={(enabled) => setC({ ...c, enabled })} />
          <CheckboxLabel label="mod" checked={c.mod_only} onChange={(mod_only) => setC({ ...c, mod_only })} />
        </div>
      </td>
      <td className="text-right">
        <div className="flex gap-1 justify-end">
          <button disabled={!dirty} onClick={() => onSave(c)} className="p-1.5 text-[hsl(var(--primary))] disabled:opacity-30" data-testid={`save-cmd-${cmd.trigger}`} title="Save">
            <Save className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(cmd)} className="p-1.5 text-[hsl(var(--destructive))]" data-testid={`delete-cmd-${cmd.trigger}`} title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function TriggerInput({ value, onChange, testId, compact = false }) {
  return (
    <div className="flex">
      <span className={`${compact ? "h-9 px-2" : "h-10 px-3"} inline-flex items-center bg-[hsl(var(--background))] border border-r-0 border-[hsl(var(--border))] rounded-l font-mono text-sm`}>!</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        data-testid={testId}
        className={`${compact ? compactInputClass : inputClass} rounded-l-none font-mono`}
      />
    </div>
  );
}

function NumberInput({ value, onChange, compact = false }) {
  return (
    <input
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value || 0, 10))}
      className={`${compact ? "w-20 " + compactInputClass : inputClass} font-mono`}
    />
  );
}

function CommandTypeSelect({ value, onChange, compact = false } = {}) {
  return (
    <select
      value={value || "text_response"}
      onChange={(e) => onChange?.(e.target.value)}
      className={`${compact ? compactInputClass : inputClass} bg-[hsl(var(--surface-2))]`}
    >
      <option value="text_response">Fixed response</option>
      <option value="ai_prompt">AI prompt</option>
    </select>
  );
}

function CheckboxLabel({ label = "", checked, onChange } = {}) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} />
      {label}
    </label>
  );
}

function Field({ label = "", children, className = "" } = {}) {
  return (
    <div className={className}>
      <div className="label-eyebrow mb-1.5">{label}</div>
      {children}
    </div>
  );
}
